import { supabase } from '../lib/supabase';
import { formatSubmissionFieldValue } from '../utils/submissionFieldFormatter';

// ─── Tenant Guard ─────────────────────────────────────────────────────────────
// Returns the impersonated user's ID when an admin impersonation session is
// active, otherwise returns the real authenticated user's ID.
const tenantGuard = async () => {
  const impersonationData = sessionStorage?.getItem('superAdminImpersonating');
  if (impersonationData) {
    try {
      const parsed = JSON.parse(impersonationData);
      if (parsed?.targetId) return parsed?.targetId;
    } catch (e) { /* fall through */ }
  }
  const { data: { user }, error } = await supabase?.auth?.getUser();
  if (error || !user) throw new Error('Not authenticated');
  return user?.id;
};

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = typeof obj?.[key] === 'object' ? toCamelCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
};

// Helper function to convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = typeof obj?.[key] === 'object' ? toSnakeCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
};

export const submissionsManagementService = {
  // Create a short-lived signed URL for a respondent-uploaded file stored in
  // the private 'form-uploads' bucket. RLS restricts reads to the form owner.
  async getFileDownloadUrl(storagePath) {
    const { data, error } = await supabase
      ?.storage
      ?.from('form-uploads')
      ?.createSignedUrl(storagePath, 3600);
    if (error) throw error;
    if (!data?.signedUrl) throw new Error('Could not create download link');
    return data.signedUrl;
  },

  // Get all submissions across all user's forms with filters
  async getAllSubmissions(filters = {}) {
    try {
      const userId = await tenantGuard();

      // Build query
      let query = supabase
        ?.from('form_submissions')
        ?.select(`
          *,
          forms!inner(id, title, user_id, form_elements),
          submission_label_assignments(label_id, submission_labels(id, name, color))
        `)
        ?.eq('forms.user_id', userId)?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.formId) {
        query = query?.eq('form_id', filters?.formId);
      }

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      if (filters?.dateFrom) {
        query = query?.gte('created_at', filters?.dateFrom);
      }

      if (filters?.dateTo) {
        query = query?.lte('created_at', filters?.dateTo);
      }

      if (filters?.labelId) {
        query = query?.contains('submission_label_assignments.label_id', [filters?.labelId]);
      }

      const { data, error } = await query;

      if (error) throw error;

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error in getAllSubmissions:', error);
      throw error;
    }
  },

  // Get all user's forms for filter dropdown
  async getUserForms() {
    try {
      const userId = await tenantGuard();

      const { data, error } = await supabase
        ?.from('forms')
        ?.select('id, title')
        ?.eq('user_id', userId)
        ?.order('title');

      if (error) throw error;

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error in getUserForms:', error);
      throw error;
    }
  },

  // Get all user's labels
  async getLabels() {
    try {
      const userId = await tenantGuard();

      const { data, error } = await supabase
        ?.from('submission_labels')?.select('*')?.eq('user_id', userId)?.order('name');

      if (error) throw error;

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error in getLabels:', error);
      throw error;
    }
  },

  // Create new label
  async createLabel(name, color) {
    try {
      const userId = await tenantGuard();

      const { data, error } = await supabase
        ?.from('submission_labels')
        ?.insert({ user_id: userId, name, color })
        ?.select()
        ?.single();

      if (error) throw error;

      return toCamelCase(data);
    } catch (error) {
      console.error('Error in createLabel:', error);
      throw error;
    }
  },

  // Assign label to submission
  async assignLabel(submissionId, labelId) {
    try {
      const { data, error } = await supabase
        ?.from('submission_label_assignments')
        ?.insert({ submission_id: submissionId, label_id: labelId })
        ?.select()
        ?.single();

      if (error) throw error;

      return toCamelCase(data);
    } catch (error) {
      console.error('Error in assignLabel:', error);
      throw error;
    }
  },

  // Remove label from submission
  async removeLabel(submissionId, labelId) {
    try {
      const { error } = await supabase
        ?.from('submission_label_assignments')?.delete()?.eq('submission_id', submissionId)?.eq('label_id', labelId);

      if (error) throw error;
    } catch (error) {
      console.error('Error in removeLabel:', error);
      throw error;
    }
  },

  // Bulk update submission status — scoped to target user's forms
  async bulkUpdateStatus(submissionIds, status) {
    try {
      const userId = await tenantGuard();

      // Only update submissions that belong to the target user's forms
      const { error } = await supabase?.from('form_submissions')?.update({ status })?.in('id', submissionIds)?.in('form_id',supabase?.from('forms')?.select('id')?.eq('user_id', userId)
        );

      if (error) throw error;
    } catch (error) {
      console.error('Error in bulkUpdateStatus:', error);
      throw error;
    }
  },

  // Bulk assign label
  async bulkAssignLabel(submissionIds, labelId) {
    try {
      const assignments = submissionIds?.map(submissionId => ({
        submission_id: submissionId,
        label_id: labelId
      }));

      const { error } = await supabase
        ?.from('submission_label_assignments')
        ?.insert(assignments);

      if (error) throw error;
    } catch (error) {
      console.error('Error in bulkAssignLabel:', error);
      throw error;
    }
  },

  // Format a single submission field value — mirrors the detail-view formatter
  _formatFieldValue(key, value, formElements) {
    return formatSubmissionFieldValue(key, value, formElements);
  },

  // Export submissions to CSV
  exportToCSV(submissions) {
    const headers = ['Form Name', 'Submission Date', 'Status', 'Source', 'Labels', 'Data'];
    const rows = submissions?.map(s => {
      const formElements = s?.forms?.formElements || [];
      const rawData = s?.submissionData || {};
      const formattedData = Object.fromEntries(
        Object.entries(rawData).map(([k, v]) => [k, this._formatFieldValue(k, v, formElements)])
      );
      return [
        s?.forms?.title || 'Unknown',
        new Date(s?.createdAt)?.toLocaleString(),
        s?.status || 'new',
        s?.source || 'Direct',
        s?.submissionLabelAssignments?.map(a => a?.submissionLabels?.name)?.join(', ') || '',
        JSON.stringify(formattedData),
      ];
    });

    const csvContent = [headers, ...rows]
      ?.map(row => row?.map(cell => `"${cell}"`)?.join(','))?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `submissions-export-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    link?.click();
    URL.revokeObjectURL(url);
  },

  // Subscribe to all submissions changes
  subscribeToAllSubmissions(userId, callback) {
    const channel = supabase
      ?.channel('all_submissions_changes')?.on('postgres_changes',
        {
          event: '*',schema: 'public',table: 'form_submissions'
        },
        (payload) => {
          callback(toCamelCase(payload));
        }
      )
      ?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  }
};